import { ChangeDetectorRef, Component, ElementRef, HostListener, OnInit, signal, ViewChild } from '@angular/core';
import { filter, forkJoin, lastValueFrom, map, Observable, startWith, Subscription, take } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { S3Service } from '../services/s3.service';
import { AlertModalComponent } from '../alert-modal/alert-modal.component';
import { Log } from '../models/prompt-log.interface';
import { DraftSectionDialogComponent } from '../draft-section-dialog/draft-section-dialog.component';
import { CollectionsService } from '../services/collections.service';
import { Model, modelList } from '../shared/llms';
import { Collection, FileMetadata } from '../models/collection.interface';
import { CollectionAttributes, FilterSets, SourceDocument } from '../models/advisor.interface';
import { Store } from '@ngrx/store';
import {
  clearSession,
  completeTypingMessage,
  loadSession,
  loadSourceDocuments,
  populateMessages,
  saveSession,
  sendMessage,
  updateUserPrompt
} from '../state/conversations/conversations.actions';
// TODO: Rename these when moving back to /advisor
import { SourceDocumentDialogNewComponent } from './source-document-dialog/source-document-dialog.component';
import { FilterModalNewComponent } from '../filter-modal/filter-modal.component';
import { AppState } from '../state/app.state';
import {
  selectActiveCollectionId,
  selectActiveSessionId,
  selectError,
  selectIsTyping,
  selectLoading,
  selectMessages,
  selectreplyFromLLM,
  selectSourceDocuments,
  selectUserPrompt
} from '../state/conversations';
import { Actions, ofType } from '@ngrx/effects';
import { NavigationStart, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormControl } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { PreviousDiscussionsModalComponent } from './previous-discussions-modal/previous-discussions-modal.component';
import { ChatMessage } from '../models/conversations.interface';
import { NavigationService } from '../services/navigation.service';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { ConversationsService, PollingStatus } from '../services/conversations.service';

const LOCAL_STORAGE_KEY = 'selected_collection';
const SELECTED_COLLECTIONS_STORAGE_KEY = 'multiple_selected_collections';

@Component({
  selector: 'app-advisor-new',
  templateUrl: './advisor-new.component.html',
  styleUrls: ['./advisor-new.component.scss']
})
export class AdvisorNewComponent implements OnInit {
  // Focus cursor back on text box after prompt is sent
  @ViewChild('promptInput') promptInputElement!: ElementRef;
  // Reference to collection menu trigger for programmatic control
  @ViewChild('collectionMenuTrigger') collectionMenuTrigger!: MatMenuTrigger;
  @ViewChild('collectionInput') collectionInput!: ElementRef<HTMLInputElement>;
  @ViewChild(MatAutocompleteTrigger) autoTrigger!: MatAutocompleteTrigger;
  @HostListener('window:blur')

  // ngrx reactive state
  activeSessionId$!: Observable<string | null>;
  activeCollectionId$!: Observable<Collection['collection_id'] | null>;
  messages$!: Observable<ChatMessage[]>;
  loading$!: Observable<boolean>;
  error$!: Observable<string | null>;
  currentTypingMessage$!: Observable<ChatMessage | null>;
  sourceDocuments$!: Observable<SourceDocument[] | null>;
  isTyping$!: Observable<boolean>;
  userPrompt$ = this.store.select(selectUserPrompt);
  session_id = signal('');

  // Polling status
  pollingStatus$!: Observable<PollingStatus | null>;

  currentSessionId: string | null = null;
  // Attributes to filter on
  collectionSelectOptions: CollectionAttributes = {};
  // Filters
  activeFilters: CollectionAttributes = {
    state: [],
    year: [],
    docket_number: []
  };
  // Prompt input box
  prompt = '';
  modelResponse = '';
  fullResponse = '';
  draft = '';
  promptSubscription?: Subscription;
  pageSizeLimit = 650;
  promptFinished = false;
  logsLoading = true;
  qaLogs: Log[] = [];
  collections: Collection[] = [];

  // Collection autocomplete
  collectionSearchControl = new FormControl('');
  filteredCollections: Observable<Collection[]> = new Observable<Collection[]>();

  // TODO: Add Type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  sourceDocuments: any[] = [];
  promptFilename = '';
  selectedCollection: Collection | undefined;
  typingIndex = 0; // Index for typewriter effect
  typingTimeout?: NodeJS.Timeout; // Timeout ID for typing effect
  models = modelList;
  selectedModel: Model | null = this.models.modelList.find(model => model.id === 'claude-4-sonnet') || null;

  selectedCollectionsArray: Collection[] = [];
  selectedIds = new Set<string>();
  trackByCollection = (_: number, collection: Collection) => collection.collection_id;

  constructor(
    public dialog: MatDialog,
    private s3Service: S3Service,
    private collectionService: CollectionsService,
    private store: Store<AppState>,
    private actions$: Actions,
    private router: Router,
    private snackbar: MatSnackBar,
    private navigationService: NavigationService,
    private cdr: ChangeDetectorRef,
    private conversationsService: ConversationsService
  ) {
    // Detect page navigation. Clear session if user navigates away, but not when page refreshes
    this.router.events.pipe(filter(event => event instanceof NavigationStart)).subscribe(() => {
      this.clearSession();
    });
  }

  ngOnInit() {
    // Session/collection selectors
    this.activeSessionId$ = this.store.select(selectActiveSessionId);
    this.activeCollectionId$ = this.store.select(selectActiveCollectionId);

    // Message-related selectors
    this.messages$ = this.store.select(selectMessages);
    this.currentTypingMessage$ = this.store.select(selectreplyFromLLM);
    this.sourceDocuments$ = this.store.select(selectSourceDocuments);
    this.isTyping$ = this.store.select(selectIsTyping);

    // Status selectors
    this.loading$ = this.store.select(selectLoading);
    this.error$ = this.store.select(selectError);

    // Polling status from ConversationsService
    this.pollingStatus$ = this.conversationsService.getPollingStatus();

    // Initialize state
    this.modelResponse = '';
    this.sourceDocuments = [];
    this.getCollections();

    // Typing
    this.userPrompt$.subscribe(prompt => (this.prompt = prompt));

    // Initialize filtered collections
    this.filteredCollections = this.collectionSearchControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterCollections(value || ''))
    );

    // Listen for typing completion
    this.actions$.pipe(ofType(completeTypingMessage)).subscribe(() => {
      this.focusOnPromptInput();
    });

    window.addEventListener('beforeunload', () => this.handleBeforeUnload());

    const previousUrl = this.navigationService.getPreviousUrl();
    const navEntry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
    const navType = navEntry?.type;

    const isReload = navType === 'reload';
    const cameFromAdvisor = previousUrl?.includes('/advisor');

    if (isReload && cameFromAdvisor) {
      this.restoreSessionIfExists();
    } else {
      this.clearSession();
      localStorage.removeItem('collection_id');
      localStorage.removeItem('session_id');
      localStorage.removeItem('collection_name');
      localStorage.removeItem('selected_collections_id');
    }

    this.filteredCollections = this.collectionSearchControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterCollections(value || ''))
    );
  }

  restoreSessionIfExists() {
    const collection_id = localStorage.getItem('collection_id');
    const collection_name = localStorage.getItem('collection_name');
    const session_id = localStorage.getItem('session_id');
    this.session_id.set(session_id!);
    const selected_collections_id_raw = localStorage.getItem('selected_collections_id');
    const selected_collections_id: string[] = selected_collections_id_raw
      ? JSON.parse(selected_collections_id_raw)
      : [];
    if (collection_id && session_id && collection_name) {
      const selected_names = selected_collections_id
        .map(id => this.collections.find(c => c.collection_id === id)?.name)
        .filter((n): n is string => !!n);

      const names = selected_names.length ? selected_names : [collection_name];

      this.setCollectionToLocalStorage(names[0], names);

      this.store.dispatch(
        loadSession({
          session_id,
          collection_id,
          collection_name,
          selected_collections_id
        })
      );
      this.fetchLogsById(session_id, collection_id);
    } else {
      console.log('No session to restore');
    }
  }

  // Save session on page unload
  handleBeforeUnload() {
    this.activeSessionId$.pipe(take(1)).subscribe(session_id => {
      console.log('Saving session on page unload: ', session_id);
      if (!session_id || !this.selectedCollection) {
        return;
      }
      this.store.dispatch(
        saveSession({
          session_id: session_id,
          collection_id: this.selectedCollection.collection_id,
          collection_name: this.selectedCollection.name,
          selected_collections_id: this.selectedCollectionsArray.map(c => c.collection_id)
        })
      );
    });
  }
  // Focus on prompt box after a response is received from the LLM
  focusOnPromptInput() {
    // Use setTimeout to ensure this runs after Angular's change detection
    setTimeout(() => {
      if (this.promptInputElement) {
        this.promptInputElement.nativeElement.focus();
      }
    }, 0);
  }

  // Render text in textbox
  onPromptChange() {
    this.store.dispatch(updateUserPrompt({ prompt: this.prompt }));
  }

  clearSession() {
    this.store.dispatch(clearSession());
  }

  sendMessage() {
    const mult_collection_id = Array.from(
      new Set(this.selectedCollectionsArray?.map(c => c.collection_id).filter(Boolean) as string[])
    );

    const collection_id =
      this.selectedCollection?.collection_id ?? (mult_collection_id.length ? mult_collection_id[0] : '');

    const model_id = this.selectedModel ? this.selectedModel.id : '';

    this.activeSessionId$.pipe(take(1)).subscribe(session_id => {
      this.store.dispatch(loadSourceDocuments({ source_documents: [] }));

      this.store.dispatch(
        sendMessage({
          model_id,
          collection_id,
          mult_collection_id,
          session_id,
          message: this.prompt,
          activeFilters: this.activeFilters
        })
      );
    });
  }

  applySelectedCollections(): void {
    // Enforce at least one selection (default to the first collection)
    if (!this.selectedCollectionsArray?.length && this.collections?.length) {
      this.selectedCollectionsArray = [this.collections[0]];
    }

    this.selectedCollectionsArray = [...this.selectedCollectionsArray].sort((a, b) => a.name.localeCompare(b.name));
    this.selectedIds = new Set(this.selectedCollectionsArray.map(c => c.collection_id));
    this.selectedCollection = this.selectedCollectionsArray[0];

    const selNames = this.selectedCollectionsArray.map(c => c.name);
    this.setCollectionToLocalStorage(selNames[0], selNames);

    this.collectionSearchControl.setValue(this.selectedCollection?.name ?? '', { emitEvent: false });

    this.setCollection(this.selectedCollection, this.selectedCollectionsArray);
  }

  changeCollection(collection: Collection | undefined, selected_collections: Collection[]): void {
    this.store.dispatch(clearSession());
    if (collection) {
      this.setCollection(collection, selected_collections);
    }
  }

  setQuestion(option: string): void {
    this.prompt = option;
    this.clearSession();
  }

  openHistory() {
    const dialogRef = this.dialog.open(PreviousDiscussionsModalComponent, {
      width: '1000px',
      data: {
        collection: this.selectedCollection,
        selected_collections_id: this.selectedCollectionsArray.map(c => c.collection_id)
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.store.dispatch(
          populateMessages({
            session_id: result.session_id,
            collection_id: result.collection_id,
            messages: result.history,
            selected_collections_id: result.selected_collections_id
          })
        );
        this.fetchLogsById(result.session_id, result.collection_id);
      }
    });
  }
  // Everything below here is using the legacy advisor code, without a state management library
  async openFilterModal(): Promise<void> {
    const dialogRef = this.dialog.open(FilterModalNewComponent, {
      width: '500px',
      data: {
        options: this.collectionSelectOptions,
        currentFilters: { ...this.activeFilters }
      }
    });

    const result: CollectionAttributes = await lastValueFrom(dialogRef.afterClosed());

    if (result) {
      console.log('Received filters from modal: ', result);
      this.activeFilters = result;
    }
  }

  get filterCount(): number {
    return Object.values(this.activeFilters).flat().length;
  }

  clearPromptResult() {
    this.modelResponse = '';
    this.fullResponse = '';
    this.sourceDocuments = [];
    this.promptFinished = false;
  }

  setTemplate(option: string): void {
    this.prompt = option;
  }

  // TODO: Add Type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  openSourceDocumentDialog(doc: any): void {
    this.dialog.open(SourceDocumentDialogNewComponent, {
      width: '1200px',
      height: 'auto',
      data: doc
    });
  }

  onCitationClick(citationId: number): void {
    // Critical: Find by citation_id, NOT array index!
    this.sourceDocuments$.pipe(take(1)).subscribe(docs => {
      if (!docs) return;

      console.log('Citation clicked: ', citationId);

      const doc = docs.find(d => d.citation_id === citationId);

      if (doc) {
        this.openSourceDocumentDialog(doc);
      } else {
        console.warn(`No source document found for citation [${citationId}]`);
      }
    });
  }

  convertDecimalToPercent(number: number): string {
    return `${(number * 100).toFixed(2)}%`;
  }

  copyToClipboard(): void {
    navigator.clipboard.writeText(this.modelResponse).then(
      () => {
        console.log('Copying to clipboard was successful!');
      },
      err => {
        console.error('Could not copy text: ', err);
      }
    );
  }

  getLogs(collection_id: string) {
    this.logsLoading = true;
    this.s3Service.getFiles('advisor', collection_id).subscribe({
      next: (data: Log[]) => {
        this.qaLogs = data;
      },
      error: error => {
        console.error('There was an error while fetching the logs: ', error);
      },
      complete: () => {
        this.logsLoading = false;
      }
    });
  }

  fetchLogsById(session_id: string, collection_id: string): void {
    this.s3Service.getFiles('advisor', collection_id).subscribe({
      next: (logs: Log[]) => {
        const log = logs.find(log => log.session_id === session_id);
        if (log?.source_documents?.length) {
          // Sort source documents by descending score
          const sortedDocs = [...log.source_documents].sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
          this.store.dispatch(loadSourceDocuments({ source_documents: sortedDocs }));
        } else {
          console.log('No log found with source documents for session_id: ', session_id);
          this.store.dispatch(loadSourceDocuments({ source_documents: [] }));
        }
      },
      error: error => {
        console.error('There was an error while fetching the logs: ', error);
      }
    });
  }

  getCollections() {
    this.collectionService.getCollections().subscribe({
      next: data => {
        this.collections = data.sort((a, b) => a.name.localeCompare(b.name));
        // Load the collection from local storage if it exists, effectively remembering the last collection

        const collection_to_load = localStorage.getItem(LOCAL_STORAGE_KEY) ?? this.collections[0]?.name ?? '';

        const selected_collection_names_raw = localStorage.getItem(SELECTED_COLLECTIONS_STORAGE_KEY);
        let selected_collection_names: string[] = [];

        if (selected_collection_names_raw) {
          try {
            const parsed = JSON.parse(selected_collection_names_raw);
            selected_collection_names = Array.isArray(parsed) ? parsed : [parsed];
          } catch {
            selected_collection_names = [selected_collection_names_raw];
          }
        }

        if (!selected_collection_names.length && collection_to_load) {
          selected_collection_names = [collection_to_load];
        } else if (!selected_collection_names.length && this.collections) {
          selected_collection_names = [this.collections[0].name];
        }

        this.selectedCollectionsArray = selected_collection_names
          .map(n => this.collections.find(c => c.name === n))
          .filter((c): c is Collection => !!c);

        console.log('getCollections - selectedCollectiondArray', this.selectedCollectionsArray);

        if (!this.selectedCollectionsArray.length && this.collections[0]) {
          this.selectedCollectionsArray = [this.collections[0]];
          console.log(
            'getCollections - defaulting to first in collection in collections',
            this.selectedCollectionsArray
          );
        }

        this.selectedIds = new Set(this.selectedCollectionsArray.map(c => c.collection_id));

        this.selectedCollection =
          this.collections.find(c => c.name === collection_to_load) ??
          this.selectedCollectionsArray[0] ??
          this.collections[0];

        if (this.selectedCollection) {
          this.setCollection(this.selectedCollection, this.selectedCollectionsArray);
          // Update the search control with the selected collection name
          this.collectionSearchControl.setValue(this.selectedCollection.name);
          return;
        }
        throw new Error(`Could not find collection ${collection_to_load}`);
      },
      error: error => {
        console.error('There was an error while fetching the collections: ', error);
      }
    });
  }

  setCollectionToLocalStorage(collectionName: string, selectedCollectionNames: string[] = []) {
    const primary = selectedCollectionNames?.length ? selectedCollectionNames[0] : collectionName;

    localStorage.setItem(LOCAL_STORAGE_KEY, primary);
    localStorage.setItem(SELECTED_COLLECTIONS_STORAGE_KEY, primary);
  }

  showSnackbar(message: string): void {
    this.snackbar.open(message, '', { duration: 2000 });
  }
  draftSection(context: string): void {
    const dialogRef = this.dialog.open(DraftSectionDialogComponent, {
      width: '1000px',
      height: '750px',
      data: {
        context: context,
        documents: this.sourceDocuments,
        filename: this.promptFilename
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      this.draft = result;
    });
  }

  openAlert(title: string, message: string): void {
    this.dialog.open(AlertModalComponent, {
      data: {
        title: title,
        message: message
      }
    });
  }

  resetLogsDisplay() {
    this.qaLogs = [];
  }

  resetFilters() {
    this.activeFilters = {
      state: [],
      year: []
    };
    this.collectionSelectOptions = {};
  }

  // Gets logs for a collection and populates filters with appropriate options
  setCollection(collection: Collection, selected_collections: Collection[]): void {
    this.selectedCollection = collection;
    if (this.selectedCollection) {
      this.collectionSearchControl.setValue(this.selectedCollection.name);
    }

    const collections_names_array = selected_collections.map(c => c.name);

    this.resetLogsDisplay();
    this.resetFilters();

    if (this.selectedCollection) {
      this.getLogs(collection.collection_id);
    }

    this.fetchFilterOptions(selected_collections ?? []);
    this.setCollectionToLocalStorage(collection.name, collections_names_array);
  }

  get hasFilterOptions(): boolean {
    return Object.keys(this.collectionSelectOptions).length > 0;
  }

  // Helper to allow filter selects to be sorted
  _setToSortedArray(set: Set<string>): string[] {
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }

  fetchFilterOptions(selected_collections: Collection[]) {
    const selected_collection_ids = selected_collections.map(c => c.collection_id);

    const validFilters: FilterSets = {};

    if (selected_collections && selected_collections.length > 0) {
      selected_collections.forEach(collection => {
        collection.dimensions
          .filter(dimension => dimension.show_advisor_filter)
          .forEach(dimension => {
            const standardized_name = dimension.name.replace(/\s+/g, '_').toLowerCase();
            if (!validFilters[standardized_name]) {
              validFilters[standardized_name] = new Set<string>();
            }
          });
      });

      const requests = selected_collection_ids.map(id => this.collectionService.getCollectionData(id));

      forkJoin(requests).subscribe({
        next: (results: FileMetadata[][]) => {
          results.forEach(data => {
            data.forEach((item: FileMetadata) => {
              const { catalogue } = item;
              if (!catalogue) return;
              Object.entries(catalogue).forEach(([key, value]) => {
                if (!value) return;
                if (key in validFilters) {
                  validFilters[key].add(value);
                }
              });
            });
          });
          Object.keys(validFilters).forEach(key => {
            this.collectionSelectOptions[key] = this._setToSortedArray(validFilters[key]);
          });
        },
        error: error => {
          console.error('There was an error while fetching the catalogue: ', error);
        }
      });
    }
  }

  // Filter collections based on search text
  private _filterCollections(value: string | Collection): Collection[] {
    if (typeof value === 'string') {
      const filterValue = value.toLowerCase();
      return this.collections.filter(
        collection =>
          collection.name.toLowerCase().includes(filterValue) ||
          collection.description.toLowerCase().includes(filterValue)
      );
    } else {
      // If value is already a Collection object, return all collections
      return this.collections;
    }
  }

  // Display function for the autocomplete to show the collection name
  displayCollectionFn(collection: Collection): string {
    return collection && collection.name ? collection.name : '';
  }

  // Handle blur event on the collection input field
  handleInputBlur(event: FocusEvent): void {
    // Use setTimeout to allow click events on autocomplete options to complete first
    setTimeout(() => {
      // Check if the newly focused element is inside the autocomplete panel
      const relatedTarget = event.relatedTarget as HTMLElement;
      const isOptionClicked =
        relatedTarget &&
        (relatedTarget.closest('.mat-mdc-autocomplete-panel') || relatedTarget.closest('.mat-mdc-option'));

      // Only close if not clicking on an option
      if (!isOptionClicked && this.collectionMenuTrigger) {
        this.collectionMenuTrigger.closeMenu();
      }
    }, 150);
  }

  // When the menu is opened, focus on the input and show all collections
  focusCollectionInput(): void {
    setTimeout(() => {
      const input = document.querySelector('.collection-search-field input') as HTMLInputElement;
      if (input) {
        // Clear the input to show all collections
        this.collectionSearchControl.setValue('');
        // Focus on the input
        input.focus();
      }
    }, 100);
  }

  selectModel(model: Model | null) {
    this.selectedModel = model;
  }

  isSelected(collection: Collection): boolean {
    return this.selectedIds.has(collection.collection_id);
  }

  onToggleCollection(c: Collection, change: MatCheckboxChange, trigger: MatAutocompleteTrigger): void {
    const checked = change.checked;

    // prevent unchecking last remaining selection
    if (!checked && this.isSelected(c) && this.selectedCollectionsArray.length === 1) {
      change.source.checked = true;
      this.snackbar.open('Must have at least one selected collection.', 'OK');
      Promise.resolve().then(() => trigger.updatePosition());
      return;
    }

    if (checked) {
      if (!this.selectedIds.has(c.collection_id)) {
        this.selectedIds.add(c.collection_id);
        // Keep array sorted by name
        this.selectedCollectionsArray = [...this.selectedCollectionsArray, c].sort((a, b) =>
          a.name.localeCompare(b.name)
        );
      }
    } else {
      if (this.selectedIds.delete(c.collection_id)) {
        this.selectedCollectionsArray = this.selectedCollectionsArray
          .filter(s => s.collection_id !== c.collection_id)
          // Keep array sorted by name
          .sort((a, b) => a.name.localeCompare(b.name));
      }
    }

    const selNames = this.selectedCollectionsArray.map(x => x.name);
    this.setCollectionToLocalStorage(selNames[0], selNames);

    this.collectionSearchControl.setValue('', { emitEvent: true });
    Promise.resolve().then(() => {
      trigger.updatePosition();
    });
  }

  get collectionPlaceHolder(): string {
    const n = this.selectedCollectionsArray.length;
    return n > 1 ? `Collections (${n})` : 'Select a collection...';
  }

  onCollectionMenuOpened(): void {
    this.collectionSearchControl.setValue('', { emitEvent: false });
  }

  onCollectionMenuClosed(): void {
    if (this.selectedCollectionsArray.length === 1) {
      const name = this.selectedCollectionsArray[0].name;
      this.collectionSearchControl.setValue(name, { emitEvent: false });
    } else {
      this.collectionSearchControl.setValue('', { emitEvent: false });
    }
  }

  get collectionButtonLabel(): string {
    const n = this.selectedCollectionsArray?.length ?? 0;
    return n <= 1 ? (this.selectedCollectionsArray[0]?.name ?? '') : `Collections (${n})`;
  }

  onChipRemoved(c: Collection, trigger: MatAutocompleteTrigger): void {
    // Block removing the last chip
    if (this.selectedCollectionsArray.length === 1) {
      this.snackbar.open('Must have at least one selected collection', 'OK');
      Promise.resolve().then(() => {
        this.collectionInput?.nativeElement.focus();
        this.cdr.detectChanges();
        trigger.openPanel();
        trigger.updatePosition();
        this.collectionMenuTrigger?.updatePosition?.();
      });
      return;
    }

    this.selectedIds.delete(c.collection_id);
    this.selectedCollectionsArray = this.selectedCollectionsArray.filter(s => s.collection_id !== c.collection_id);

    const selNames = this.selectedCollectionsArray.map(x => x.name);
    this.setCollectionToLocalStorage(selNames[0], selNames);

    this.collectionSearchControl.setValue('', { emitEvent: true });

    Promise.resolve().then(() => {
      this.collectionInput?.nativeElement.focus();
      this.cdr.detectChanges();
      trigger.openPanel();
      trigger.updatePosition();
      this.collectionMenuTrigger?.updatePosition?.();
    });
  }

  onWindowBlur(): void {
    this.autoTrigger?.closePanel();
    this.collectionMenuTrigger?.closeMenu();
  }
}
